import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { formatCurrency } from '../../utils/formatters';
import { DollarSign, CheckCircle, Clock, ChevronDown, ChevronUp, Calendar, Wallet, Download, X } from 'lucide-react';

// Pay period anchor: Dec 15, 2025
const PAY_PERIOD_ANCHOR = new Date(2025, 11, 15); // Dec 15, 2025
const PAY_PERIOD_DAYS = 14;

// Parse date string as local date (not UTC)
function parseLocalDate(dateStr) {
  const datePart = String(dateStr).substring(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatDateShort(date) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(date) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function formatDayDate(dateStr) {
  const d = parseLocalDate(dateStr);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
}

function getPayPeriodByIndex(index) {
  const periodStart = new Date(PAY_PERIOD_ANCHOR);
  periodStart.setDate(periodStart.getDate() + (index * PAY_PERIOD_DAYS));
  
  const periodEnd = new Date(periodStart);
  periodEnd.setDate(periodEnd.getDate() + PAY_PERIOD_DAYS - 1);
  
  return { index, start: periodStart, end: periodEnd };
}

function getPayPeriodFromDate(dateStr) {
  const d = parseLocalDate(dateStr);
  const diffTime = d.getTime() - PAY_PERIOD_ANCHOR.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const periodIndex = Math.floor(diffDays / PAY_PERIOD_DAYS);
  return getPayPeriodByIndex(periodIndex);
}

function getCurrentPayPeriod() {
  const now = new Date();
  const diffTime = now.getTime() - PAY_PERIOD_ANCHOR.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const periodIndex = Math.floor(diffDays / PAY_PERIOD_DAYS);
  return getPayPeriodByIndex(periodIndex);
}

// Calculate expected payroll date (Wednesday after 2 weeks of invoicing)
// First payroll: Jan 14, 2026 for Dec 15-28 period
function getExpectedPayrollDate(periodEnd) {
  // Payroll runs ~2 weeks after period ends (waiting for MGM payments)
  const payrollDate = new Date(periodEnd);
  payrollDate.setDate(payrollDate.getDate() + 14);
  // Adjust to next Wednesday if not already Wednesday
  const dayOfWeek = payrollDate.getDay();
  const daysUntilWednesday = (3 - dayOfWeek + 7) % 7;
  if (daysUntilWednesday > 0) {
    payrollDate.setDate(payrollDate.getDate() + daysUntilWednesday);
  }
  return payrollDate;
}

function PeriodCard({ period, expanded, onToggle, isCurrentPeriod, isFuturePeriod }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Separate worked vs upcoming gigs
  const workedGigs = period.assignments.filter(g => {
    const gigDate = parseLocalDate(g.date);
    gigDate.setHours(0, 0, 0, 0);
    return gigDate < today;
  });
  
  const upcomingGigs = period.assignments.filter(g => {
    const gigDate = parseLocalDate(g.date);
    gigDate.setHours(0, 0, 0, 0);
    return gigDate >= today;
  });

  const earnedTotal = workedGigs.reduce((sum, g) => sum + (parseFloat(g.payout) || 0), 0);
  const pendingTotal = upcomingGigs.reduce((sum, g) => sum + (parseFloat(g.payout) || 0), 0);
  
  // Check if all worked gigs are paid
  const allPaid = workedGigs.length > 0 && workedGigs.every(g => g.paidAt);
  const payrollDate = allPaid && workedGigs.length > 0 
    ? workedGigs.reduce((earliest, g) => {
        const paidDate = new Date(g.paidAt);
        return !earliest || paidDate < earliest ? paidDate : earliest;
      }, null)
    : null;

  const expectedPayroll = getExpectedPayrollDate(period.end);

  return (
    <div className="card">
      <div className="card-body">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={onToggle}
        >
          <div>
            <p className="font-medium">{period.label}</p>
            <p className="text-sm text-gray-500">
              {period.assignments.length} gig{period.assignments.length !== 1 ? 's' : ''}
              {isCurrentPeriod && <span className="ml-2 text-hub-600">(current period)</span>}
            </p>
            {/* Payroll status */}
            {allPaid && payrollDate ? (
              <p className="text-xs text-blue-600 mt-1">
                <CheckCircle className="w-3 h-3 inline mr-1" />
                Submitted to payroll: {formatDateFull(payrollDate)}
              </p>
            ) : !isFuturePeriod && workedGigs.length > 0 ? (
              <p className="text-xs text-gray-500 mt-1">
                <Clock className="w-3 h-3 inline mr-1" />
                Expected payroll: {formatDateFull(expectedPayroll)}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-lg font-bold text-green-600">{formatCurrency(earnedTotal)}</p>
              {pendingTotal > 0 && (
                <p className="text-sm text-yellow-600">+{formatCurrency(pendingTotal)} pending</p>
              )}
            </div>
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
        
        {expanded && (
          <div className="mt-4 pt-4 border-t space-y-2">
            {/* Worked gigs - green for worked, blue for submitted */}
            {workedGigs.map(a => (
              <div key={a.id || a.assignmentId} className={`flex items-center justify-between text-sm p-2 rounded ${
                a.paidAt 
                  ? 'bg-blue-50 border border-blue-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div>
                  <p className={`font-medium ${a.paidAt ? 'text-blue-800' : 'text-green-800'}`}>{formatDayDate(a.date)}</p>
                  <p className={a.paidAt ? 'text-blue-700' : 'text-green-700'}>{a.venue} - {a.location}</p>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${a.paidAt ? 'text-blue-700' : 'text-green-700'}`}>{formatCurrency(a.payout)}</p>
                  {a.paidAt ? (
                    <p className="text-xs text-blue-600">✓ Submitted {formatDateShort(new Date(a.paidAt))}</p>
                  ) : (
                    <p className="text-xs text-green-600">✓ Worked</p>
                  )}
                </div>
              </div>
            ))}
            
            {/* Upcoming gigs - yellow */}
            {upcomingGigs.map(a => (
              <div key={a.id || a.assignmentId} className="flex items-center justify-between text-sm p-2 bg-yellow-50 border border-yellow-200 rounded">
                <div>
                  <p className="font-medium text-yellow-800">{formatDayDate(a.date)}</p>
                  <p className="text-yellow-700">{a.venue} - {a.location}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-yellow-700">({formatCurrency(a.payout)})</p>
                  <p className="text-xs text-yellow-600">Scheduled</p>
                </div>
              </div>
            ))}

            {/* Period totals */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Earned this period:</span>
                <span className="font-semibold text-green-600">{formatCurrency(earnedTotal)}</span>
              </div>
              {pendingTotal > 0 && (
                <>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-600">Pending (if all performed):</span>
                    <span className="font-semibold text-yellow-600">{formatCurrency(pendingTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1 pt-1 border-t">
                    <span className="text-gray-700 font-medium">Period total:</span>
                    <span className="font-bold">{formatCurrency(earnedTotal + pendingTotal)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MonthCard({ month, expanded, onToggle }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const isCurrentMonth = month.year === today.getFullYear() && month.month === today.getMonth();

  // Separate worked vs upcoming
  const workedGigs = month.assignments.filter(g => {
    const gigDate = parseLocalDate(g.date);
    gigDate.setHours(0, 0, 0, 0);
    return gigDate < today;
  });
  
  const upcomingGigs = month.assignments.filter(g => {
    const gigDate = parseLocalDate(g.date);
    gigDate.setHours(0, 0, 0, 0);
    return gigDate >= today;
  });

  const earnedTotal = workedGigs.reduce((sum, g) => sum + (parseFloat(g.payout) || 0), 0);
  const pendingTotal = upcomingGigs.reduce((sum, g) => sum + (parseFloat(g.payout) || 0), 0);

  return (
    <div className="card">
      <div className="card-body">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={onToggle}
        >
          <div>
            <p className="font-medium">{month.label}</p>
            <p className="text-sm text-gray-500">
              {month.assignments.length} gig{month.assignments.length !== 1 ? 's' : ''}
              {isCurrentMonth && <span className="ml-2 text-hub-600">(in progress)</span>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-lg font-bold text-green-600">{formatCurrency(earnedTotal)}</p>
              {pendingTotal > 0 && (
                <p className="text-sm text-yellow-600">+{formatCurrency(pendingTotal)} pending</p>
              )}
            </div>
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
        
        {expanded && (
          <div className="mt-4 pt-4 border-t space-y-2">
            {/* Worked gigs - green for worked, blue for submitted */}
            {workedGigs.map(a => (
              <div key={a.id || a.assignmentId} className={`flex items-center justify-between text-sm p-2 rounded ${
                a.paidAt 
                  ? 'bg-blue-50 border border-blue-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div>
                  <p className={`font-medium ${a.paidAt ? 'text-blue-800' : 'text-green-800'}`}>{formatDayDate(a.date)}</p>
                  <p className={a.paidAt ? 'text-blue-700' : 'text-green-700'}>{a.venue} - {a.location}</p>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${a.paidAt ? 'text-blue-700' : 'text-green-700'}`}>{formatCurrency(a.payout)}</p>
                  {a.paidAt ? (
                    <p className="text-xs text-blue-600">✓ Submitted {formatDateShort(new Date(a.paidAt))}</p>
                  ) : (
                    <p className="text-xs text-green-600">✓ Worked</p>
                  )}
                </div>
              </div>
            ))}
            
            {/* Upcoming gigs - yellow */}
            {upcomingGigs.map(a => (
              <div key={a.id || a.assignmentId} className="flex items-center justify-between text-sm p-2 bg-yellow-50 border border-yellow-200 rounded">
                <div>
                  <p className="font-medium text-yellow-800">{formatDayDate(a.date)}</p>
                  <p className="text-yellow-700">{a.venue} - {a.location}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-yellow-700">({formatCurrency(a.payout)})</p>
                  <p className="text-xs text-yellow-600">Scheduled</p>
                </div>
              </div>
            ))}

            {/* Month totals */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Earned:</span>
                <span className="font-semibold text-green-600">{formatCurrency(earnedTotal)}</span>
              </div>
              {pendingTotal > 0 && (
                <>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-600">Pending:</span>
                    <span className="font-semibold text-yellow-600">{formatCurrency(pendingTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1 pt-1 border-t">
                    <span className="text-gray-700 font-medium">Month total:</span>
                    <span className="font-bold">{formatCurrency(earnedTotal + pendingTotal)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ContractorEarnings() {
  const { user } = useAuth();
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [viewMode, setViewMode] = useState('period');
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportRange, setExportRange] = useState('6months');
  const [exportVenue, setExportVenue] = useState('');
  const [exportLocation, setExportLocation] = useState('');
  const [venues, setVenues] = useState([]);
  const [locations, setLocations] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get(`/contractors/${user.id}/gigs`)
      .then(data => {
        // Include ALL accepted gigs (past and future) that aren't declined
        const accepted = data.filter(g => g.status !== 'DECLINED');
        setGigs(accepted);
        
        // Auto-expand current pay period
        const currentPeriod = getCurrentPayPeriod();
        const currentKey = `${currentPeriod.start.getFullYear()}-${String(currentPeriod.start.getMonth() + 1).padStart(2, '0')}-${String(currentPeriod.start.getDate()).padStart(2, '0')}`;
        setExpanded({ [currentKey]: true });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    
    // Load venues and locations for export filter
    api.get('/exports/my-gigs/venues')
      .then(data => {
        if (data && data.venues) {
          setVenues(data.venues);
          setLocations(data.locations || []);
        }
      })
      .catch(() => {
        setVenues([]);
        setLocations([]);
      });
  }, [user.id]);

  // Group by pay period
  const payPeriods = useMemo(() => {
    const grouped = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentPeriod = getCurrentPayPeriod();
    
    gigs.forEach(gig => {
      const period = getPayPeriodFromDate(gig.date);
      const key = `${period.start.getFullYear()}-${String(period.start.getMonth() + 1).padStart(2, '0')}-${String(period.start.getDate()).padStart(2, '0')}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          key,
          index: period.index,
          start: period.start,
          end: period.end,
          label: `${formatDateShort(period.start)} - ${formatDateFull(period.end)}`,
          assignments: [],
          isCurrentPeriod: period.index === currentPeriod.index,
          isFuturePeriod: period.index > currentPeriod.index
        };
      }
      
      grouped[key].assignments.push({
        ...gig,
        venue: gig.venue,
        location: gig.location,
        payout: gig.payout
      });
    });

    // Sort assignments within each period by date
    Object.values(grouped).forEach(period => {
      period.assignments.sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));
    });

    // Sort periods by start date (most recent first)
    return Object.values(grouped).sort((a, b) => b.start - a.start);
  }, [gigs]);

  // Group by month
  const months = useMemo(() => {
    const grouped = {};
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    
    gigs.forEach(gig => {
      const d = parseLocalDate(gig.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          key,
          year: d.getFullYear(),
          month: d.getMonth(),
          label: `${monthNames[d.getMonth()]} ${d.getFullYear()}`,
          assignments: []
        };
      }
      
      grouped[key].assignments.push({
        ...gig,
        venue: gig.venue,
        location: gig.location,
        payout: gig.payout
      });
    });

    // Sort assignments within each month by date
    Object.values(grouped).forEach(month => {
      month.assignments.sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));
    });

    // Sort months (most recent first)
    return Object.values(grouped).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  }, [gigs]);

  const toggleExpand = (key) => {
    setExpanded(p => ({ ...p, [key]: !p[key] }));
  };

  // Calculate totals
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const totalEarned = gigs
    .filter(g => parseLocalDate(g.date) < today)
    .reduce((sum, g) => sum + (parseFloat(g.payout) || 0), 0);
  
  const totalPending = gigs
    .filter(g => parseLocalDate(g.date) >= today)
    .reduce((sum, g) => sum + (parseFloat(g.payout) || 0), 0);

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format: exportFormat });
      const now = new Date();
      const currentPeriod = getCurrentPayPeriod();
      
      if (exportRange === 'current') {
        params.set('periodIndex', currentPeriod.index);
      } else if (exportRange === 'last') {
        params.set('periodIndex', currentPeriod.index - 1);
      } else if (exportRange === '3months') {
        const d = new Date();
        d.setMonth(d.getMonth() - 3);
        params.set('startDate', d.toISOString().substring(0, 10));
        params.set('endDate', now.toISOString().substring(0, 10));
      } else if (exportRange === '6months') {
        const d = new Date();
        d.setMonth(d.getMonth() - 6);
        params.set('startDate', d.toISOString().substring(0, 10));
        params.set('endDate', now.toISOString().substring(0, 10));
      } else if (exportRange === 'year') {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 1);
        params.set('startDate', d.toISOString().substring(0, 10));
        params.set('endDate', now.toISOString().substring(0, 10));
      }
      
      if (exportLocation) {
        params.set('locationId', exportLocation);
      } else if (exportVenue) {
        params.set('venueId', exportVenue);
      }
      
      const response = await fetch(`https://api.hubtractor.com/api/exports/my-gigs?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `my-gigs.${exportFormat}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setShowExportModal(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Pay</h1>
        <button
          onClick={() => setShowExportModal(true)}
          className="btn-secondary text-sm flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Export
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card bg-green-50 border-green-200">
          <div className="card-body text-center py-3">
            <p className="text-sm text-green-700">All-Time Earned</p>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(totalEarned)}</p>
          </div>
        </div>
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="card-body text-center py-3">
            <p className="text-sm text-yellow-700">Upcoming</p>
            <p className="text-2xl font-bold text-yellow-700">{formatCurrency(totalPending)}</p>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => setViewMode('period')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            viewMode === 'period' 
              ? 'bg-white text-hub-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Wallet className="w-4 h-4" />
          Pay Period
        </button>
        <button
          onClick={() => setViewMode('month')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            viewMode === 'month' 
              ? 'bg-white text-hub-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Month
        </button>
      </div>

      {/* Content */}
      {viewMode === 'period' ? (
        <div>
          <p className="text-sm text-gray-500 mb-3">2-week periods starting Dec 15</p>
          {payPeriods.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-8">
                <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No gigs scheduled</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {payPeriods.map(period => (
                <PeriodCard
                  key={period.key}
                  period={period}
                  expanded={expanded[period.key]}
                  onToggle={() => toggleExpand(period.key)}
                  isCurrentPeriod={period.isCurrentPeriod}
                  isFuturePeriod={period.isFuturePeriod}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          <p className="text-sm text-gray-500 mb-3">Monthly totals</p>
          {months.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-8">
                <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No gigs scheduled</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {months.map(month => (
                <MonthCard
                  key={month.key}
                  month={month}
                  expanded={expanded[month.key]}
                  onToggle={() => toggleExpand(month.key)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Export My Gigs</h2>
              <button onClick={() => setShowExportModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="label">Format</label>
                <div className="flex gap-2">
                  {[{ value: 'pdf', label: 'PDF' }, { value: 'xlsx', label: 'Excel' }, { value: 'csv', label: 'CSV' }].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setExportFormat(opt.value)}
                      className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                        exportFormat === opt.value
                          ? 'bg-hub-600 text-white border-hub-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="label">Date Range</label>
                <select value={exportRange} onChange={(e) => setExportRange(e.target.value)} className="input">
                  <option value="current">Current Pay Period</option>
                  <option value="last">Last Pay Period</option>
                  <option value="3months">Last 3 Months</option>
                  <option value="6months">Last 6 Months</option>
                  <option value="year">Last Year</option>
                  <option value="all">All Time</option>
                </select>
              </div>
              
              <div>
                <label className="label">Venue (Optional)</label>
                <select
                  value={exportVenue}
                  onChange={(e) => { setExportVenue(e.target.value); setExportLocation(''); }}
                  className="input"
                >
                  <option value="">All Venues</option>
                  {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              
              {exportVenue && (
                <div>
                  <label className="label">Location (Optional)</label>
                  <select value={exportLocation} onChange={(e) => setExportLocation(e.target.value)} className="input">
                    <option value="">All Locations</option>
                    {locations.filter(l => l.venueId === exportVenue).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex gap-3">
              <button onClick={() => setShowExportModal(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleExport} disabled={exporting} className="btn-primary flex-1 flex items-center justify-center gap-2">
                <Download className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Download'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
